'use client';

import { ResourceSection } from '@context/explorer/types';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import EndpointSelector from './EndpointSelector';
import { PromptPlaceholder } from './PromptPlaceholder';
import ResourceSelector from './ResourceSelector';
import { Resource } from '@context/openapi/types';

/*
  Flow of active navigation item:
  - By default first section (customers) is set as active
  - In ApiManagement component, setActiveId is called when any card section reaches the top 40% of the viewport
  - Sidebar makes use of activeId to highlight the active section by making use of `layout` animation from motion
*/

interface APISelectorProps {
  resources: Resource[];
  enablePromptPlaceholder: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  hideApiSelector: boolean;
}

const APISelector: React.FC<APISelectorProps> = ({
  resources,
  enablePromptPlaceholder = false,
  textareaRef,
  hideApiSelector,
}) => {
  const [activeResource, setActiveResource] = useState<string>();
  const [scrolledTop, setIsScrolledTop] = useState<boolean>(false);

  useEffect(() => {
    if (resources.length) {
      setActiveResource(resources[0].id);
    }
  }, [resources]);

  const distributeItemsEvenly = useMemo((): ResourceSection[] => {
    return resources.map((resource) => {
      const total = resource.operations.length;
      const groups = [];
      if (total <= 9) {
        const groupSizes = [];
        const baseSize = Math.floor(total / 3);
        let remainder = total % 3;

        for (let i = 0; i < 3; i++) {
          groupSizes.push(baseSize + (remainder > 0 ? 1 : 0));
          remainder--;
        }

        let index = 0;
        for (const size of groupSizes) {
          groups.push(
            resource.operations.slice(index, index + size).map((operation) => ({
              resource_id: resource.id,
              operation_id: operation.id,
              tag: operation.name,
              title: <>{operation.shortDescription || operation.description}</>,
            }))
          );
          index += size;
        }
      } else {
        let index = 0;
        while (index < total) {
          const size = Math.min(4, total - index);
          groups.push(
            resource.operations.slice(index, index + size).map((operation) => ({
              resource_id: resource.id,
              operation_id: operation.id,
              tag: operation.name,
              title: <>{operation.shortDescription || operation.description}</>,
            }))
          );
          index += size;
        }
      }

      const sec = groups.map((group, index) => ({
        id: index + 1,
        cards: group.reverse(),
      }));
      return {
        id: resource.id,
        items: sec,
      };
    });
  }, [resources]);

  const operationGroups = useMemo(
    () => distributeItemsEvenly,
    [distributeItemsEvenly]
  );

  return (
    <div>
      <motion.div
        key="documentation"
        style={{ display: hideApiSelector ? 'none' : 'block' }}
        initial={false}
        animate={{
          opacity: 1,
          y: 0,
          transition: { delay: 0.5 },
        }}
      >
        <div className={`grid grid-cols-[2.5fr,9fr] gap-8`}>
          <div
            className={`relative  text-black after:fixed after:bottom-0 after:w-full after:h-[120px] `}
          >
            {enablePromptPlaceholder && (
              <div className="fixed top-12">
                <PromptPlaceholder
                  setIsScrolledTop={setIsScrolledTop}
                  textareaRef={textareaRef}
                />
              </div>
            )}
            {activeResource && (
              <ResourceSelector
                activeResource={activeResource}
                resources={resources}
                isExpanded={enablePromptPlaceholder}
              />
            )}
          </div>
          <div className="p-9 pb-[58vh] scroll-smooth">
            {activeResource && (
              <EndpointSelector
                activeResource={activeResource}
                scrolledTop={scrolledTop}
                setActiveResource={setActiveResource}
                operationGroups={operationGroups}
              />
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default APISelector;
